import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import hostlink from "../Hostlink/hostlink";
import { toast } from "react-toastify";

const EmployeeStatement = () => {

    const user = JSON.parse(
        localStorage.getItem("MYPCKARTUSER") || "[]"
    );

    const shopid = user?.[0]?.id || 0;

    const today = new Date();

    const firstDay = new Date(
        today.getFullYear(),
        today.getMonth(),
        1
    );

    const [employees, setEmployees] = useState([]);
    const [employeeid, setEmployeeid] = useState("");

    const [from, setFrom] = useState(
        firstDay.toISOString().split("T")[0]
    );

    const [to, setTo] = useState(
        today.toISOString().split("T")[0]
    );

    const [statement, setStatement] = useState([]);
    const [loading, setLoading] = useState(false);


    /* =====================================================
       LOAD EMPLOYEES
    ===================================================== */

    useEffect(() => {

        if (shopid) {
            loadEmployees();
        }

    }, [shopid]);


    const loadEmployees = async () => {

        try {

            const response = await axios.get(
                `${hostlink}/list/${shopid}`
            );

            setEmployees(response.data || []);

        } catch (error) {

            console.error(error);

            toast.error(
                "Unable to load employees"
            );
        }
    };


    /* =====================================================
       LOAD STATEMENT
    ===================================================== */

    const loadStatement = async () => {

        if (!employeeid) {

            toast.error(
                "Please select employee"
            );

            return;
        }

        if (!from || !to) {

            toast.error(
                "Please select date range"
            );

            return;
        }

        if (from > to) {

            toast.error(
                "From date cannot be greater than To date"
            );

            return;
        }

        try {

            setLoading(true);

            const response = await axios.get(
                `${hostlink}/employee-statement/employee`,
                {
                    params: {
                        shopid,
                        employeeid,
                        from,
                        to
                    }
                }
            );

            setStatement(
                response.data || []
            );

        } catch (error) {

            console.error(
                "Statement Error:",
                error.response?.data || error
            );

            toast.error(
                "Unable to load employee statement"
            );

            setStatement([]);

        } finally {

            setLoading(false);
        }
    };


    /* =====================================================
       SELECTED EMPLOYEE
    ===================================================== */

    const selectedEmployee =
        employees.find(
            employee =>
                String(employee.id) ===
                String(employeeid)
        );


    /* =====================================================
       SORT STATEMENT
    ===================================================== */

    const sortedStatement = useMemo(() => {

        return [...statement].sort((a, b) => {

            const dateA =
                new Date(a.date || "1900-01-01");

            const dateB =
                new Date(b.date || "1900-01-01");

            return dateA - dateB;

        });

    }, [statement]);


    /* =====================================================
       SALARY TOTAL
    ===================================================== */

    const totalSalary = useMemo(() => {

        return sortedStatement
            .filter(
                item =>
                    String(
                        item.type || ""
                    ).toUpperCase() === "SALARY"
            )
            .reduce(
                (sum, item) =>
                    sum + Number(item.debit || 0),
                0
            );

    }, [sortedStatement]);


    /* =====================================================
       ADVANCE GIVEN
    ===================================================== */

    const totalAdvance = useMemo(() => {

        return sortedStatement
            .filter(
                item =>
                    String(
                        item.type || ""
                    ).toUpperCase() === "ADVANCE"
            )
            .reduce(
                (sum, item) =>
                    sum + Number(item.debit || 0),
                0
            );

    }, [sortedStatement]);


    /* =====================================================
       ADVANCE RECOVERY
    ===================================================== */

    const totalRecovery = useMemo(() => {

        return sortedStatement
            .filter(
                item =>
                    String(
                        item.type || ""
                    ).toUpperCase() === "RECOVERY"
            )
            .reduce(
                (sum, item) =>
                    sum + Number(item.credit || 0),
                0
            );

    }, [sortedStatement]);


    /* =====================================================
       ADVANCE BALANCE

       Salary is NOT included.

       Advance Given - Advance Recovery
    ===================================================== */

    const advanceBalance =
        totalAdvance - totalRecovery;


    /* =====================================================
       RUNNING ADVANCE BALANCE

       SALARY:
           No effect

       ADVANCE:
           + debit

       RECOVERY:
           - credit
    ===================================================== */

    const statementWithBalance = useMemo(() => {

        let balance = 0;

        return sortedStatement.map(item => {

            const type =
                String(
                    item.type || ""
                ).toUpperCase();

            if (type === "ADVANCE") {

                balance += Number(
                    item.debit || 0
                );
            }

            if (type === "RECOVERY") {

                balance -= Number(
                    item.credit || 0
                );
            }

            return {
                ...item,
                advanceBalance: balance
            };

        });

    }, [sortedStatement]);


    /* =====================================================
       TOTAL DEBIT

       This is only for table total.
       It may contain salary + advance.
    ===================================================== */

    const totalDebit = useMemo(() => {

        return sortedStatement.reduce(
            (sum, item) =>
                sum + Number(item.debit || 0),
            0
        );

    }, [sortedStatement]);


    /* =====================================================
       TOTAL CREDIT
    ===================================================== */

    const totalCredit = useMemo(() => {

        return sortedStatement.reduce(
            (sum, item) =>
                sum + Number(item.credit || 0),
            0
        );

    }, [sortedStatement]);


    /* =====================================================
       FORMAT AMOUNT
    ===================================================== */

    const formatAmount = amount => {

        return Number(
            amount || 0
        ).toLocaleString(
            "en-IN",
            {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }
        );
    };


    /* =====================================================
       FORMAT DATE
    ===================================================== */

    const formatDate = date => {

        if (!date) return "-";

        const d = new Date(date);

        if (isNaN(d.getTime())) {
            return date;
        }

        return d.toLocaleDateString(
            "en-IN",
            {
                day: "2-digit",
                month: "short",
                year: "numeric"
            }
        );
    };


    /* =====================================================
       TYPE
    ===================================================== */

    const getType = item => {

        return String(
            item?.type || ""
        ).toUpperCase();
    };


    /* =====================================================
       TYPE CLASS
    ===================================================== */

    const getTypeClass = type => {

        const value =
            String(
                type || ""
            ).toUpperCase();

        if (value === "SALARY") {
            return "transaction salary";
        }

        if (value === "ADVANCE") {
            return "transaction advance";
        }

        if (value === "RECOVERY") {
            return "transaction recovery";
        }

        return "transaction";
    };


    /* =====================================================
       TRANSACTION ICON
    ===================================================== */

    const getTransactionIcon = type => {

        const value =
            String(
                type || ""
            ).toUpperCase();

        if (value === "SALARY") {

            return (
                <i className="fa-solid fa-money-check-dollar"></i>
            );
        }

        if (value === "ADVANCE") {

            return (
                <i className="fa-solid fa-hand-holding-dollar"></i>
            );
        }

        return (
            <i className="fa-solid fa-rotate-left"></i>
        );
    };


    return (

        <div className="employee-statement-page">


            {/* =================================================
                HEADER
            ================================================= */}

            <div className="statement-header">

                <div className="header-content">

                    <div className="header-icon">

                        <i className="fa-solid fa-file-invoice-dollar"></i>

                    </div>

                    <div>

                        <h3>
                            Employee Statement
                        </h3>

                        <p>
                            Salary, advance & recovery statement
                        </p>

                    </div>

                </div>


                <button
                    className="print-btn"
                    onClick={() => window.print()}
                >

                    <i className="fa-solid fa-print"></i>

                    Print

                </button>

            </div>


            {/* =================================================
                FILTER
            ================================================= */}

            <div className="filter-card">

                <div className="filter-grid">


                    {/* EMPLOYEE */}

                    <div className="filter-item">

                        <label>

                            <i className="fa-solid fa-user"></i>

                            Employee

                        </label>


                        <select
                            value={employeeid}
                            onChange={e =>
                                setEmployeeid(
                                    e.target.value
                                )
                            }
                        >

                            <option value="">
                                Select Employee
                            </option>


                            {employees.map(
                                employee => (

                                    <option
                                        key={employee.id}
                                        value={employee.id}
                                    >

                                        {
                                            employee.accname ||
                                            employee.name ||
                                            `Employee ${employee.id}`
                                        }

                                    </option>

                                )
                            )}

                        </select>

                    </div>


                    {/* FROM */}

                    <div className="filter-item">

                        <label>

                            <i className="fa-regular fa-calendar"></i>

                            From Date

                        </label>


                        <input
                            type="date"
                            value={from}
                            onChange={e =>
                                setFrom(
                                    e.target.value
                                )
                            }
                        />

                    </div>


                    {/* TO */}

                    <div className="filter-item">

                        <label>

                            <i className="fa-regular fa-calendar"></i>

                            To Date

                        </label>


                        <input
                            type="date"
                            value={to}
                            onChange={e =>
                                setTo(
                                    e.target.value
                                )
                            }
                        />

                    </div>


                    {/* BUTTON */}

                    <div className="filter-action">

                        <button
                            onClick={loadStatement}
                            disabled={loading}
                        >

                            {loading ? (

                                <>
                                    <span className="spinner-border spinner-border-sm"></span>

                                    Loading
                                </>

                            ) : (

                                <>
                                    <i className="fa-solid fa-magnifying-glass"></i>

                                    View Statement
                                </>

                            )}

                        </button>

                    </div>

                </div>

            </div>


            {/* =================================================
                EMPLOYEE INFO
            ================================================= */}

            {employeeid && (

                <div className="employee-info">

                    <div className="employee-avatar">

                        <i className="fa-solid fa-user"></i>

                    </div>


                    <div>

                        <div className="employee-name">

                            {
                                selectedEmployee?.accname ||
                                selectedEmployee?.name ||
                                `Employee ${employeeid}`
                            }

                        </div>


                        <div className="employee-sub">

                            Employee ID:
                            {" "}
                            {employeeid}

                            <span>
                                •
                            </span>

                            {from}
                            {" → "}
                            {to}

                        </div>

                    </div>

                </div>

            )}


            {/* =================================================
                SUMMARY CARDS
            ================================================= */}

            {employeeid && (

                <div className="summary-grid">


                    {/* SALARY */}

                    <div className="summary-card salary-card">

                        <div className="summary-icon">

                            <i className="fa-solid fa-money-check-dollar"></i>

                        </div>


                        <div>

                            <span>
                                Salary Paid
                            </span>

                            <strong>
                                ₹ {formatAmount(
                                    totalSalary
                                )}
                            </strong>

                        </div>

                    </div>


                    {/* ADVANCE */}

                    <div className="summary-card advance-card">

                        <div className="summary-icon">

                            <i className="fa-solid fa-hand-holding-dollar"></i>

                        </div>


                        <div>

                            <span>
                                Advance Given
                            </span>

                            <strong>
                                ₹ {formatAmount(
                                    totalAdvance
                                )}
                            </strong>

                        </div>

                    </div>


                    {/* RECOVERY */}

                    <div className="summary-card recovery-card">

                        <div className="summary-icon">

                            <i className="fa-solid fa-rotate-left"></i>

                        </div>


                        <div>

                            <span>
                                Advance Recovery
                            </span>

                            <strong>
                                ₹ {formatAmount(
                                    totalRecovery
                                )}
                            </strong>

                        </div>

                    </div>


                    {/* ADVANCE BALANCE */}

                    <div className="summary-card balance-card">

                        <div className="summary-icon">

                            <i className="fa-solid fa-scale-balanced"></i>

                        </div>


                        <div>

                            <span>
                                Advance Balance
                            </span>

                            <strong>

                                ₹{" "}
                                {formatAmount(
                                    Math.abs(
                                        advanceBalance
                                    )
                                )}

                                {advanceBalance !== 0 && (

                                    <small>

                                        {
                                            advanceBalance > 0
                                                ? " Due"
                                                : " Excess"
                                        }

                                    </small>

                                )}

                            </strong>

                        </div>

                    </div>


                </div>

            )}


            {/* =================================================
                STATEMENT
            ================================================= */}

            <div className="statement-card">


                {/* TITLE */}

                <div className="statement-title">

                    <div>

                        <h5>

                            <i className="fa-solid fa-list me-2"></i>

                            Transaction Statement

                        </h5>

                        <span>
                            Salary and employee advance transactions
                        </span>

                    </div>


                    {statement.length > 0 && (

                        <div className="transaction-count">

                            {statement.length}
                            {" "}
                            Transactions

                        </div>

                    )}

                </div>


                {/* TABLE */}

                <div className="table-responsive">

                    <table className="statement-table">


                        <thead>

                            <tr>

                                <th>
                                    Date
                                </th>

                                <th>
                                    Particular
                                </th>

                                <th>
                                    Narration
                                </th>

                                <th className="amount">
                                    Debit
                                </th>

                                <th className="amount">
                                    Credit
                                </th>

                                <th className="amount">
                                    Advance Balance
                                </th>

                            </tr>

                        </thead>


                        <tbody>


                            {/* LOADING */}

                            {loading ? (

                                <tr>

                                    <td
                                        colSpan="6"
                                        className="loading-cell"
                                    >

                                        <div className="spinner-border"></div>

                                        <span>
                                            Loading statement...
                                        </span>

                                    </td>

                                </tr>


                            ) : statementWithBalance.length === 0 ? (


                                /* EMPTY */

                                <tr>

                                    <td
                                        colSpan="6"
                                        className="empty-cell"
                                    >

                                        <div className="empty-icon">

                                            <i className="fa-solid fa-file-circle-xmark"></i>

                                        </div>

                                        <strong>
                                            No transactions found
                                        </strong>

                                        <span>
                                            Select an employee and date range to view the statement.
                                        </span>

                                    </td>

                                </tr>


                            ) : (


                                /* DATA */

                                statementWithBalance.map(
                                    (item, index) => {

                                        const type =
                                            getType(item);

                                        return (

                                            <tr
                                                key={index}
                                            >


                                                {/* DATE */}

                                                <td>

                                                    <div className="date-cell">

                                                        <strong>

                                                            {formatDate(
                                                                item.date
                                                            )}

                                                        </strong>

                                                    </div>

                                                </td>


                                                {/* PARTICULAR */}

                                                <td>

                                                    <div
                                                        className={
                                                            getTypeClass(
                                                                type
                                                            )
                                                        }
                                                    >

                                                        <div className="transaction-icon">

                                                            {
                                                                getTransactionIcon(
                                                                    type
                                                                )
                                                            }

                                                        </div>


                                                        <div>

                                                            <strong>

                                                                {
                                                                    item.particular ||
                                                                    type ||
                                                                    "-"
                                                                }

                                                            </strong>

                                                            <small>
                                                                {type}
                                                            </small>

                                                        </div>

                                                    </div>

                                                </td>


                                                {/* NARRATION */}

                                                <td>

                                                    <span className="narration">

                                                        {
                                                            item.narration ||
                                                            "-"
                                                        }

                                                    </span>

                                                </td>


                                                {/* DEBIT */}

                                                <td className="amount debit">

                                                    {
                                                        Number(
                                                            item.debit || 0
                                                        ) > 0

                                                            ? (
                                                                <>
                                                                    ₹{" "}
                                                                    {formatAmount(
                                                                        item.debit
                                                                    )}
                                                                </>
                                                            )

                                                            : "-"
                                                    }

                                                </td>


                                                {/* CREDIT */}

                                                <td className="amount credit">

                                                    {
                                                        Number(
                                                            item.credit || 0
                                                        ) > 0

                                                            ? (
                                                                <>
                                                                    ₹{" "}
                                                                    {formatAmount(
                                                                        item.credit
                                                                    )}
                                                                </>
                                                            )

                                                            : "-"
                                                    }

                                                </td>


                                                {/* ADVANCE BALANCE */}

                                                <td className="amount balance">


                                                    {
                                                        type === "SALARY"

                                                            ? (

                                                                <span className="not-applicable">
                                                                    —
                                                                </span>

                                                            )

                                                            : (

                                                                <>

                                                                    <strong>

                                                                        ₹{" "}
                                                                        {formatAmount(
                                                                            Math.abs(
                                                                                item.advanceBalance
                                                                            )
                                                                        )}

                                                                    </strong>


                                                                    {item.advanceBalance !== 0 && (

                                                                        <small>

                                                                            {
                                                                                item.advanceBalance > 0
                                                                                    ? "Due"
                                                                                    : "Excess"
                                                                            }

                                                                        </small>

                                                                    )}

                                                                </>

                                                            )
                                                    }

                                                </td>

                                            </tr>

                                        );

                                    }
                                )

                            )}

                        </tbody>


                        {/* =================================================
                            TABLE FOOTER
                        ================================================= */}

                        {statement.length > 0 && (

                            <tfoot>

                                <tr>

                                    <th
                                        colSpan="3"
                                        className="total-label"
                                    >

                                        Total

                                    </th>


                                    <th className="amount debit">

                                        ₹{" "}

                                        {formatAmount(
                                            totalDebit
                                        )}

                                    </th>


                                    <th className="amount credit">

                                        ₹{" "}

                                        {formatAmount(
                                            totalCredit
                                        )}

                                    </th>


                                    <th className="amount balance">

                                        ₹{" "}

                                        {formatAmount(
                                            Math.abs(
                                                advanceBalance
                                            )
                                        )}

                                    </th>

                                </tr>

                            </tfoot>

                        )}

                    </table>

                </div>


                {/* =================================================
                    BOTTOM SUMMARY
                ================================================= */}

                {statement.length > 0 && (

                    <div className="statement-footer">


                        <div>

                            <span>
                                Salary Paid
                            </span>

                            <strong>

                                ₹{" "}
                                {formatAmount(
                                    totalSalary
                                )}

                            </strong>

                        </div>


                        <div>

                            <span>
                                Advance Given
                            </span>

                            <strong>

                                ₹{" "}
                                {formatAmount(
                                    totalAdvance
                                )}

                            </strong>

                        </div>


                        <div>

                            <span>
                                Advance Recovery
                            </span>

                            <strong className="green">

                                ₹{" "}
                                {formatAmount(
                                    totalRecovery
                                )}

                            </strong>

                        </div>


                        <div>

                            <span>
                                Advance Balance
                            </span>

                            <strong>

                                ₹{" "}
                                {formatAmount(
                                    Math.abs(
                                        advanceBalance
                                    )
                                )}

                                {advanceBalance !== 0 && (

                                    <small>

                                        {
                                            advanceBalance > 0
                                                ? " Due"
                                                : " Excess"
                                        }

                                    </small>

                                )}

                            </strong>

                        </div>


                    </div>

                )}

            </div>


            {/* =================================================
                STYLE
            ================================================= */}

            <style>{`

                * {
                    box-sizing: border-box;
                }

                .employee-statement-page {
                    min-height: 100vh;
                    padding: 20px;
                    background: #f4f6fa;
                    color: #202735;
                }

                /* HEADER */

                .statement-header {
                    min-height: 82px;
                    padding: 18px 22px;
                    border-radius: 18px;
                    background:
                        linear-gradient(
                            135deg,
                            #151c2c,
                            #293f62
                        );
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    color: white;
                    margin-bottom: 16px;
                    box-shadow:
                        0 8px 25px
                        rgba(20,30,50,.12);
                }

                .header-content {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                }

                .header-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 14px;
                    background:
                        rgba(255,255,255,.12);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 21px;
                }

                .statement-header h3 {
                    margin: 0;
                    font-size: 21px;
                    font-weight: 750;
                    letter-spacing: -.3px;
                }

                .statement-header p {
                    margin: 4px 0 0;
                    color:
                        rgba(255,255,255,.65);
                    font-size: 12px;
                }

                .print-btn {
                    border: 1px solid
                        rgba(255,255,255,.25);
                    background:
                        rgba(255,255,255,.08);
                    color: white;
                    padding: 9px 14px;
                    border-radius: 10px;
                    font-size: 12px;
                    display: flex;
                    align-items: center;
                    gap: 7px;
                    transition: .2s;
                }

                .print-btn:hover {
                    background:
                        rgba(255,255,255,.16);
                }

                /* FILTER */

                .filter-card {
                    background: white;
                    padding: 18px;
                    border-radius: 16px;
                    margin-bottom: 16px;
                    box-shadow:
                        0 5px 22px
                        rgba(25,35,55,.055);
                }

                .filter-grid {
                    display: grid;
                    grid-template-columns:
                        minmax(240px, 1.6fr)
                        minmax(170px, 1fr)
                        minmax(170px, 1fr)
                        170px;
                    gap: 14px;
                    align-items: end;
                }

                .filter-item label {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    margin-bottom: 7px;
                    color: #727c8d;
                    font-size: 11px;
                    font-weight: 700;
                }

                .filter-item input,
                .filter-item select {
                    width: 100%;
                    height: 42px;
                    border: 1px solid #e1e5eb;
                    border-radius: 10px;
                    padding: 0 12px;
                    background: white;
                    color: #293142;
                    font-size: 13px;
                    outline: none;
                    transition: .2s;
                }

                .filter-item input:focus,
                .filter-item select:focus {
                    border-color: #657895;
                    box-shadow:
                        0 0 0 3px
                        rgba(41,63,98,.07);
                }

                .filter-action button {
                    width: 100%;
                    height: 42px;
                    border: 0;
                    border-radius: 10px;
                    background: #1d2637;
                    color: white;
                    font-size: 12px;
                    font-weight: 650;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: 8px;
                    transition: .2s;
                }

                .filter-action button:hover {
                    background: #293f62;
                }

                .filter-action button:disabled {
                    opacity: .65;
                    cursor: not-allowed;
                }

                /* EMPLOYEE */

                .employee-info {
                    background: white;
                    border-radius: 15px;
                    padding: 14px 18px;
                    margin-bottom: 16px;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    box-shadow:
                        0 5px 22px
                        rgba(25,35,55,.055);
                }

                .employee-avatar {
                    width: 43px;
                    height: 43px;
                    border-radius: 12px;
                    background: #edf1f7;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #293f62;
                }

                .employee-name {
                    font-weight: 750;
                    font-size: 14px;
                }

                .employee-sub {
                    margin-top: 3px;
                    color: #8a93a2;
                    font-size: 11px;
                }

                .employee-sub span {
                    margin: 0 8px;
                }

                /* SUMMARY */

                .summary-grid {
                    display: grid;
                    grid-template-columns:
                        repeat(4, 1fr);
                    gap: 14px;
                    margin-bottom: 16px;
                }

                .summary-card {
                    background: white;
                    min-height: 92px;
                    border-radius: 15px;
                    padding: 16px;
                    display: flex;
                    align-items: center;
                    gap: 13px;
                    box-shadow:
                        0 5px 22px
                        rgba(25,35,55,.055);
                    border-left:
                        4px solid transparent;
                }

                .summary-icon {
                    width: 43px;
                    height: 43px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #f2f4f7;
                    font-size: 17px;
                }

                .summary-card span {
                    display: block;
                    color: #8992a0;
                    font-size: 11px;
                    margin-bottom: 4px;
                }

                .summary-card strong {
                    display: block;
                    font-size: 18px;
                    font-weight: 800;
                    color: #252c39;
                }

                .summary-card strong small {
                    font-size: 10px;
                    color: #8d95a2;
                    margin-left: 4px;
                }

                .salary-card {
                    border-left-color: #4361ee;
                }

                .advance-card {
                    border-left-color: #f0a202;
                }

                .recovery-card {
                    border-left-color: #198754;
                }

                .balance-card {
                    border-left-color: #293f62;
                }

                /* STATEMENT */

                .statement-card {
                    background: white;
                    border-radius: 17px;
                    overflow: hidden;
                    box-shadow:
                        0 5px 22px
                        rgba(25,35,55,.055);
                }

                .statement-title {
                    padding: 17px 20px;
                    border-bottom:
                        1px solid #edf0f4;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }

                .statement-title h5 {
                    margin: 0;
                    font-size: 14px;
                    font-weight: 750;
                }

                .statement-title span {
                    display: block;
                    margin-top: 3px;
                    color: #929aa7;
                    font-size: 10px;
                }

                .transaction-count {
                    background: #f2f4f7;
                    padding: 6px 10px;
                    border-radius: 20px;
                    color: #6f7887;
                    font-size: 10px;
                    font-weight: 650;
                }

                /* TABLE */

                .statement-table {
                    width: 100%;
                    border-collapse: collapse;
                }

                .statement-table thead th {
                    padding: 12px 15px;
                    background: #f8f9fb;
                    color: #778191;
                    border-bottom:
                        1px solid #edf0f4;
                    font-size: 9px;
                    text-transform: uppercase;
                    letter-spacing: .5px;
                    font-weight: 750;
                    white-space: nowrap;
                }

                .statement-table tbody td {
                    padding: 13px 15px;
                    border-bottom:
                        1px solid #f0f2f5;
                    font-size: 12px;
                    vertical-align: middle;
                }

                .statement-table tbody tr:hover {
                    background: #fafbfd;
                }

                .amount {
                    text-align: right;
                    white-space: nowrap;
                }

                .date-cell strong {
                    font-size: 11px;
                    font-weight: 650;
                    color: #444d5d;
                }

                /* TRANSACTION */

                .transaction {
                    display: flex;
                    align-items: center;
                    gap: 9px;
                }

                .transaction-icon {
                    width: 32px;
                    height: 32px;
                    border-radius: 9px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #f1f3f6;
                    color: #6e7887;
                    flex-shrink: 0;
                }

                .transaction strong {
                    display: block;
                    font-size: 11px;
                    font-weight: 700;
                }

                .transaction small {
                    display: block;
                    margin-top: 2px;
                    font-size: 8px;
                    color: #9aa2af;
                    text-transform: uppercase;
                }

                .transaction.salary .transaction-icon {
                    background: #eef1ff;
                    color: #4361ee;
                }

                .transaction.advance .transaction-icon {
                    background: #fff5df;
                    color: #d58b00;
                }

                .transaction.recovery .transaction-icon {
                    background: #eaf7f0;
                    color: #198754;
                }

                .narration {
                    color: #737c8c;
                    font-size: 11px;
                }

                .debit {
                    color: #333b48;
                    font-weight: 650;
                }

                .credit {
                    color: #198754;
                    font-weight: 650;
                }

                .balance strong {
                    color: #252c39;
                }

                .balance small {
                    margin-left: 4px;
                    color: #929aa7;
                    font-size: 9px;
                }

                .not-applicable {
                    color: #aeb5bf;
                    font-size: 16px;
                }

                /* FOOTER */

                .statement-table tfoot th {
                    padding: 14px 15px;
                    background: #f7f8fa;
                    border-top:
                        1px solid #e6e9ee;
                    font-size: 11px;
                }

                .total-label {
                    color: #515a68;
                }

                /* BOTTOM SUMMARY */

                .statement-footer {
                    display: flex;
                    justify-content: flex-end;
                    gap: 35px;
                    padding: 15px 20px;
                    border-top:
                        1px solid #edf0f4;
                    background: #fcfcfd;
                }

                .statement-footer > div {
                    text-align: right;
                }

                .statement-footer span {
                    display: block;
                    color: #8d96a3;
                    font-size: 9px;
                    text-transform: uppercase;
                    letter-spacing: .4px;
                }

                .statement-footer strong {
                    display: block;
                    margin-top: 3px;
                    color: #303746;
                    font-size: 14px;
                }

                .statement-footer strong.green {
                    color: #198754;
                }

                .statement-footer small {
                    color: #8d96a3;
                    font-size: 9px;
                    margin-left: 3px;
                }

                /* EMPTY */

                .empty-cell {
                    padding: 65px 20px !important;
                    text-align: center;
                }

                .empty-icon {
                    width: 55px;
                    height: 55px;
                    margin: auto;
                    border-radius: 16px;
                    background: #f2f4f7;
                    color: #b4bbc5;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 22px;
                }

                .empty-cell strong {
                    display: block;
                    margin-top: 12px;
                    color: #5e6775;
                    font-size: 13px;
                }

                .empty-cell span {
                    display: block;
                    margin-top: 5px;
                    color: #a0a7b2;
                    font-size: 10px;
                }

                /* LOADING */

                .loading-cell {
                    padding: 60px 20px !important;
                    text-align: center;
                    color: #8c95a3;
                }

                .loading-cell .spinner-border {
                    display: block;
                    margin: 0 auto 10px;
                }

                .loading-cell span {
                    font-size: 11px;
                }

                /* RESPONSIVE */

                @media(max-width: 1100px) {

                    .filter-grid {
                        grid-template-columns:
                            1fr 1fr;
                    }

                    .filter-action {
                        grid-column: span 2;
                    }

                    .summary-grid {
                        grid-template-columns:
                            1fr 1fr;
                    }

                    .statement-footer {
                        flex-wrap: wrap;
                    }

                }

                @media(max-width: 700px) {

                    .employee-statement-page {
                        padding: 10px;
                    }

                    .statement-header {
                        padding: 15px;
                    }

                    .statement-header h3 {
                        font-size: 17px;
                    }

                    .print-btn {
                        padding: 8px 10px;
                    }

                    .filter-grid {
                        grid-template-columns: 1fr;
                    }

                    .filter-action {
                        grid-column: auto;
                    }

                    .summary-grid {
                        grid-template-columns: 1fr;
                    }

                    .statement-title {
                        align-items: flex-start;
                        gap: 10px;
                    }

                    .statement-footer {
                        flex-direction: column;
                        gap: 12px;
                    }

                    .statement-footer > div {
                        text-align: left;
                    }

                }

                /* PRINT */

                @media print {

                    .employee-statement-page {
                        padding: 0;
                        background: white;
                    }

                    .statement-header {
                        border-radius: 0;
                        box-shadow: none;
                    }

                    .print-btn,
                    .filter-card {
                        display: none !important;
                    }

                    .employee-info {
                        box-shadow: none;
                        border: 1px solid #ddd;
                    }

                    .summary-grid {
                        grid-template-columns:
                            repeat(4, 1fr);
                    }

                    .summary-card {
                        box-shadow: none;
                        border: 1px solid #ddd;
                    }

                    .statement-card {
                        box-shadow: none;
                        border: 1px solid #ddd;
                    }

                    .statement-table tbody tr:hover {
                        background: white;
                    }

                }

            `}</style>

        </div>
    );
};

export default EmployeeStatement;